import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Gate header size toggles while the shrink/expand animation runs
   * @type {boolean}
   */
  #sizeAnimating = false;

  /** @type {number | null} */
  #sizeUnlockTimeout = null;

  /**
   * Timeouts to coordinate post-shrink recenter animation for stacked logo
   * @type {number | null}
   */
  #recenterToAnimatingTimeout = null;
  /** @type {number | null} */
  #recenterToCenteredTimeout = null;
  /**
   * Timeout used to end the shrink deferral after logo2 slide completes
   * @type {number | null}
   */
  #deferShrinkTimeout = null;
  /**
   * Timeout used to unlock scroll after the full animation sequence ends
   * @type {number | null}
   */
  #scrollUnlockTimeout = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Parses a CSS duration string (e.g. '300ms' or '0.3s') into milliseconds
   * @param {string} value
   * @returns {number}
   */
  #parseDurationToMs(value) {
    if (!value) return 300;
    const v = String(value).trim();
    if (v.endsWith('ms')) return parseFloat(v);
    if (v.endsWith('s')) return parseFloat(v) * 1000;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 300;
  }

  /**
   * Reads --header-shrink-duration from computed styles and returns ms
   * @returns {number}
   */
  #getShrinkDurationMs() {
    const style = getComputedStyle(this);
    const raw = style.getPropertyValue('--header-shrink-duration');
    return this.#parseDurationToMs(raw || '300ms');
  }

  /**
   * Reads --header-recenter-duration from computed styles and returns ms
   * @returns {number}
   */
  #getRecenterDurationMs() {
    const style = getComputedStyle(this);
    const raw = style.getPropertyValue('--header-recenter-duration');
    return this.#parseDurationToMs(raw || '150ms');
  }

  /**
   * Reads --header-recenter-overlap from computed styles and returns ms
   * Overlap amount determines how much earlier than shrink end we start recentering
   * @returns {number}
   */
  #getRecenterOverlapMs() {
    const style = getComputedStyle(this);
    const raw = style.getPropertyValue('--header-recenter-overlap');
    return this.#parseDurationToMs(raw || '80ms');
  }

  /**
   * Parses a CSS pixel string (e.g. '120px') into a number of pixels
   * @param {string} value
   * @returns {number}
   */
  #parsePixels(value) {
    if (!value) return NaN;
    const v = String(value).trim();
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
  }

  /**
   * Read scroll thresholds from CSS variables set on the host
   * @returns {{ shrink: number, expand: number }}
   */
  #getScrollThresholdsPx() {
    const style = getComputedStyle(this);
    const shrinkRaw = style.getPropertyValue('--header-scroll-shrink-threshold');
    const expandRaw = style.getPropertyValue('--header-scroll-expand-threshold');
    const shrink = this.#parsePixels(shrinkRaw);
    const expand = this.#parsePixels(expandRaw);
    // Provide sensible defaults if CSS variables are missing
    return {
      shrink: Number.isFinite(shrink) ? shrink : 120,
      expand: Number.isFinite(expand) ? expand : 8,
    };
  }

  /**
   * Reads --logo2-slide-duration from computed styles and returns ms
   * Falls back to --header-shrink-duration
   * @returns {number}
   */
  #getLogo2SlideDurationMs() {
    const style = getComputedStyle(this);
    const raw = style.getPropertyValue('--logo2-slide-duration').trim();
    if (raw) return this.#parseDurationToMs(raw);
    const total = this.#getShrinkDurationMs();
    const ratio = 0.4; // default: 40% of the global duration for the slide
    return Math.max(0, Math.round(total * ratio));
  }

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
        // Do not toggle size here; rely on scroll hysteresis to avoid flicker
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    const desktopDrawerEnabled = this.dataset.desktopDrawer === 'true' || this.dataset.desktopDrawer === '1';
    if (desktopDrawerEnabled) {
      // With desktop drawer enabled, always hide the horizontal menu and show the drawer
      this.#updateMenuVisibility(true);
    } else {
      this.#updateMenuVisibility(event.detail.minimumReached);
    }
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    const desktopDrawerEnabled = this.dataset.desktopDrawer === 'true' || this.dataset.desktopDrawer === '1';

    if (hideMenu || desktopDrawerEnabled) {
      // Always show drawer when menu overflows
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;

      // Hide the header menu if desktop drawer is enabled or when overflow hides menu
      this.refs.headerMenu.classList.add('hidden');
    } else {
      // If desktop drawer is enabled, keep it visible even on desktop
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    // Direction-based hysteresis via CSS-configurable thresholds
    const { expand: upExpandThreshold, shrink: downShrinkThreshold } = this.#getScrollThresholdsPx();
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    // Hysteresis: apply thresholds by direction
    if (this.dataset.size === 'normal' && scrollTop <= upExpandThreshold) {
      this.#setSize('large');
    } else if (this.dataset.size !== 'normal' && scrollTop >= downShrinkThreshold) {
      this.#setSize('normal');
    }

    // For sticky behavior below, retain the previous gating
    if (!this.#offscreen && stickyMode !== 'always') {
      this.#lastScrollTop = scrollTop;
      return;
    }

    if (stickyMode === 'always') {
      // Use scrollTop to determine size state to avoid sticky bounding rect quirks
      if (scrollTop <= upExpandThreshold) {
        this.dataset.scrollDirection = 'none';
        this.#setSize('large');
      } else if (scrollTop >= downShrinkThreshold) {
        this.dataset.scrollDirection = isScrollingUp ? 'up' : 'down';
        this.#setSize('normal');
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        if (scrollTop <= upExpandThreshold) this.#setSize('large');
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        if (scrollTop >= downShrinkThreshold) this.#setSize('normal');
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
      if (scrollTop >= downShrinkThreshold) this.#setSize('normal');
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');
    }

    // Always track scroll to update header size state, regardless of sticky mode
    document.addEventListener('scroll', this.#handleWindowScroll);

    // Initialize size state based on initial scroll with configured thresholds
    const { shrink: downShrinkThreshold } = this.#getScrollThresholdsPx();
    const initialScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#setSize(initialScrollTop >= downShrinkThreshold ? 'normal' : 'large');

    // Initialize visibility based on desktop drawer setting
    const desktopDrawerEnabled = this.dataset.desktopDrawer === 'true' || this.dataset.desktopDrawer === '1';
    this.#updateMenuVisibility(desktopDrawerEnabled);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }

  /**
   * Update header size state dataset attribute
   * @param {'large'|'normal'} size
   */
  #setSize(size) {
    // Ignore if already set
    if (this.dataset.size === size) return;

    // Prevent reversing mid-transition: wait until the current animation finishes
    if (this.#sizeAnimating) return;

    // Defer setting dataset.size until after we attach listeners for the target state

    // Start animation lock for the duration of slide + recenter + shrink (prevents mid-sequence flips)
    this.#sizeAnimating = true;
    this.setAttribute('data-size-animating', '');
    // Parallel timeline: slide and shrink run concurrently
    const shrinkDuration = this.#getShrinkDurationMs();
    const slideDuration = this.#getLogo2SlideDurationMs();
    const swapDelay = slideDuration; // fallback only; primary trigger is transitionend
    const sequenceDuration = Math.max(slideDuration, shrinkDuration);

    // Do not override CSS variables for durations; rely on theme settings.
    if (this.#sizeUnlockTimeout) clearTimeout(this.#sizeUnlockTimeout);
    this.#sizeUnlockTimeout = setTimeout(() => {
      this.#sizeAnimating = false;
      this.removeAttribute('data-size-animating');
      // Clear per-run slide base var
      this.style.removeProperty('--logo2-slide-height-base');
      this.style.removeProperty('--logo1-slide-height-base');
    }, sequenceDuration + 120);

    // Clear any existing recenter timers (no longer used with crossfade approach)
    if (this.#recenterToAnimatingTimeout) { clearTimeout(this.#recenterToAnimatingTimeout); this.#recenterToAnimatingTimeout = null; }
    if (this.#recenterToCenteredTimeout) { clearTimeout(this.#recenterToCenteredTimeout); this.#recenterToCenteredTimeout = null; }

    // Clear any pending defer-shrink timers
    if (this.#deferShrinkTimeout) {
      clearTimeout(this.#deferShrinkTimeout);
      this.#deferShrinkTimeout = null;
    }
    // Clear any pending scroll unlock timers
    if (this.#scrollUnlockTimeout) {
      clearTimeout(this.#scrollUnlockTimeout);
      this.#scrollUnlockTimeout = null;
    }

    // Reset state when returning to large size
    if (size === 'large') {
      this.removeAttribute('data-recenter-state');
      this.removeAttribute('data-defer-shrink');
      this.removeAttribute('data-logo-mode');
      document.documentElement.removeAttribute('scroll-lock');
      this.dataset.size = 'large';
      return;
    }

    // For normal size, play in this order with parallelism:
    // 1) Start header shrink immediately
    // 2) Start logo2 slide-up (stack visible) with fixed slide base
    // 3) Swap to single-top exactly when slide ends
    if (size === 'normal') {
      // Step 0: reset any recenter state (not used) and lock scroll for the full sequence
      this.removeAttribute('data-recenter-state');
      document.documentElement.setAttribute('scroll-lock', '');

      // Primary path: switch to single-top exactly on slide transition end
      const bottomImg = this.querySelector('.header-logo__stack-bottom .header-logo__stack-img');
      const topImg = this.querySelector('.header-logo__stack-top .header-logo__stack-img');
      // Compute a fixed base for slide distance before size toggles
      if (bottomImg instanceof HTMLElement) {
        const rect = bottomImg.getBoundingClientRect();
        const h = Math.round(rect.height || parseFloat(getComputedStyle(bottomImg).height) || 0);
        if (h > 0) this.style.setProperty('--logo2-slide-height-base', `${h}px`);
      }
      // Compute a fixed base for the top row height to anchor the absolute bottom row
      if (topImg instanceof HTMLElement) {
        const rectTop = topImg.getBoundingClientRect();
        const hTop = Math.round(rectTop.height || parseFloat(getComputedStyle(topImg).height) || 0);
        if (hTop > 0) this.style.setProperty('--logo1-slide-height-base', `${hTop}px`);
      }
      const onSlideEnd = (/** @type {TransitionEvent} */ e) => {
        if (e.propertyName !== 'transform') return;
        // Use event currentTarget to avoid referencing possibly-null bottomImg
        e.currentTarget && (e.currentTarget).removeEventListener('transitionend', onSlideEnd);
        if (this.dataset.size !== 'normal') return;
        this.dataset.logoMode = 'single';
        // Nothing else to gate; shrink already running in parallel
      };
      if (bottomImg) bottomImg.addEventListener('transitionend', onSlideEnd);

      // Now toggle to normal to start the slide (listener already attached)
      this.dataset.size = 'normal';

      // Fallbacks based on computed CSS transition timing for transform
      /**
       * Parse CSS time string (e.g., "300ms", "0.3s") to milliseconds
       * @param {string} v
       */
      const toMs = (v) => {
        v = (v || '').trim();
        if (v.endsWith('ms')) return parseFloat(v);
        if (v.endsWith('s')) return parseFloat(v) * 1000;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      /**
       * Compute effective transform transition time (delay + duration) from computed styles
       * @param {HTMLElement | null} el
       */
      const getTransformTransitionMs = (el) => {
        if (!el) return swapDelay;
        const cs = getComputedStyle(el);
        const props = cs.transitionProperty.split(',').map((s) => s.trim());
        const durations = cs.transitionDuration.split(',').map(toMs);
        const delays = cs.transitionDelay.split(',').map(toMs);
        let idx = props.findIndex((p) => p === 'transform' || p === 'all');
        if (idx === -1) idx = 0;
        const dur = durations[idx] ?? durations[0] ?? 0;
        const del = delays[idx] ?? delays[0] ?? 0;
        return del + dur;
      };

      const computedSlideMs = getTransformTransitionMs(bottomImg instanceof HTMLElement ? bottomImg : null);

      // Fallback: perform swap on timeout if transitionend didn't fire (delay beyond computed slide duration)
      setTimeout(() => {
        if (this.dataset.size !== 'normal') return;
        if (!this.dataset.logoMode) this.dataset.logoMode = 'single';
      }, computedSlideMs + 50);

      // Unlock scroll after the entire sequence (slide + shrink)
      this.#scrollUnlockTimeout = setTimeout(() => {
        document.documentElement.removeAttribute('scroll-lock');
      }, sequenceDuration + 120);
    }
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
